import React, { useState, useEffect } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import '../css/Earnings.css';
import '../css/Shimmer.css';
import { earningsAPI } from '../services/api';
import { ShimmerStatCards, ShimmerChart, ShimmerServicesList, ShimmerTable } from '../components/Shimmer';

// Import icons
import grossEarningIcon from '../assets/images/Driverimages/total-driver.svg';
import netEarningIcon from '../assets/images/Driverimages/active-driver.svg';
import completedJobsIcon from '../assets/images/Driverimages/completed-jobs.svg';
import commissionIcon from '../assets/images/Driverimages/av-driver-rating.svg';
import earningsEmptyIcon from '../assets/images/EmptyIcons/Earnings-empty.svg';

// Bar chart colors for services
const CHART_COLORS = ['#8362E5', '#FFB3E9', '#9DD9D2', '#D1B5FF', '#F4D06F', '#FEC196', '#FF6B6B', '#4ECDC4'];

const Earnings = () => {
  const [loading, setLoading] = useState(true);
  const [chartLoading, setChartLoading] = useState(true);
  const [transactionsLoading, setTransactionsLoading] = useState(true);
  const [earningsData, setEarningsData] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  // Fetch earnings and transactions data
  useEffect(() => {
    fetchEarningsData();
    fetchChartData();
    fetchTransactions(1);
  }, []);

  useEffect(() => {
    if (currentPage > 1) {
      fetchTransactions(currentPage);
    }
  }, [currentPage]);

  const fetchEarningsData = async () => {
    try {
      setLoading(true);
      const response = await earningsAPI.getEarnings();
      if (response.data.Status === 'Success') {
        setEarningsData(response.data);
      }
    } catch (error) {
      // Error fetching earnings
    } finally {
      setLoading(false);
    }
  };

  const fetchChartData = async () => {
    try {
      setChartLoading(true);
      const response = await earningsAPI.getBookingsCount();
      if (response.data) {
        setChartData(response.data);
      }
    } catch (error) {
      // Error fetching chart data
    } finally {
      setChartLoading(false);
    }
  };

  const fetchTransactions = async (page) => {
    try {
      setTransactionsLoading(true);
      const response = await earningsAPI.getTransactions({ page });
      if (response.data.Status === 'Success') {
        setTransactions(response.data.Transactions || []);
        setPagination(response.data.Pagination);
      }
    } catch (error) {
      // Error fetching transactions
    } finally {
      setTransactionsLoading(false);
    }
  };

  // Format currency
  const formatCurrency = (value) => {
    return `$${Number(value || 0).toLocaleString()}`;
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB');
  };

  // Generate stats cards from API data
  const getStatsCards = () => {
    const summary = earningsData?.Summary || {};
    return [
      {
        id: 1,
        title: 'Gross Earnings',
        value: formatCurrency(summary.GrossEarnings),
        borderColor: '#6366F1',
        icon: grossEarningIcon
      },
      {
        id: 2,
        title: 'Net Earnings (After fees)',
        value: formatCurrency(summary.NetEarnings),
        borderColor: '#EC4899',
        icon: netEarningIcon
      },
      {
        id: 3,
        title: 'Completed Jobs',
        value: summary.CompletedJobs || 0,
        borderColor: '#F59E0B',
        icon: completedJobsIcon
      },
      {
        id: 4,
        title: `Commission (${summary.CommissionRate || '0%'})`,
        value: formatCurrency(summary.Commission),
        borderColor: '#10B981',
        icon: commissionIcon
      }
    ];
  };

  // Get services orders from API
  const servicesOrders = earningsData?.ServiceOrders || [];

  // Generate chart data from API
  const getBarChartData = () => {
    if (!chartData?.earnings_by_service?.length) return [];

    const maxPercentage = Math.max(...chartData.earnings_by_service.map(s => s.percentage || 0), 1);
    const maxHeight = 228; // Maximum bar height in pixels

    return chartData.earnings_by_service.map((service, index) => ({
      name: service.service_name,
      value: service.percentage || 0,
      height: Math.max(Math.round((service.percentage / maxPercentage) * maxHeight), 20),
      color: CHART_COLORS[index % CHART_COLORS.length]
    }));
  };

  const barChartData = getBarChartData();
  const chartPeriodLabel = chartData?.period?.label || 'Last 30 days';

  const statsCards = getStatsCards();

  
  return (
    <div className="earnings-page">
      <Container fluid className="px-4 py-4">
        {/* Stats Cards Row */}
        {loading ? (
          <ShimmerStatCards count={4} />
        ) : (
          <Row className="mb-4">
            {statsCards.map((card) => (
              <Col lg={3} md={6} sm={6} xs={12} key={card.id} className="mb-3">
                <div
                  className="earnings-stat-card"
                  style={{ borderLeft: `4px solid ${card.borderColor}` }}
                >
                  <div className="earnings-stat-content">
                    <p className="earnings-stat-title">{card.title}</p>
                    <h2 className="earnings-stat-value">{card.value}</h2>
                  </div>
                  <div className="earnings-stat-icon">
                    <img src={card.icon} alt={card.title} />
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        )}

        {/* Chart and Services Orders Row */}
        <Row className="mb-4">
          {/* Earnings by Service Type Chart */}
          <Col lg={8} className="mb-3">
            {chartLoading ? (
              <ShimmerChart />
            ) : (
              <div className="earnings-chart-card">
                <div className="earnings-chart-header">
                  <h3 className="earnings-chart-title">Earnings By Service Type</h3>
                  <span className="earnings-chart-subtitle">Share of gross earnings ({chartPeriodLabel})</span>
                </div>

                <div className="earnings-chart-body">
                  {barChartData.length > 0 ? (
                    <>
                      {/* Custom Bar Chart */}
                      <div className="custom-bar-chart">
                        {barChartData.map((item, index) => (
                          <div key={index} className="custom-bar-item">
                            <div className="custom-bar-label">
                              <span className="custom-bar-percent">{item.value}%</span>
                              <span className="custom-bar-name">{item.name}</span>
                            </div>
                            <div
                              className="custom-bar"
                              style={{
                                height: `${item.height}px`,
                                backgroundColor: item.color
                              }}
                            ></div>
                          </div>
                        ))}
                      </div>

                      {/* Chart Legend */}
                      <div className="earnings-chart-legend">
                        {barChartData.map((item, index) => (
                          <div key={index} className="earnings-legend-item">
                            <span
                              className="earnings-legend-dot"
                              style={{ backgroundColor: item.color }}
                            ></span>
                            <span>{item.name}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <div className="earnings-empty-state chart-empty">
                      <img src={earningsEmptyIcon} alt="No Earnings" className="empty-state-icon" />
                      <h3 className="empty-state-title">No Chart Data</h3>
                      <p className="empty-state-text">
                        Earnings data will appear here once you complete bookings.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </Col>

          {/* Services Orders */}
          <Col lg={4} className="mb-3">
            {loading ? (
              <ShimmerServicesList count={6} />
            ) : (
              <div className="earnings-services-card">
                <div className="earnings-services-header">
                  <h3 className="earnings-services-title">Services Orders</h3>
                </div>
                <div className="earnings-services-body">
                  {servicesOrders.length > 0 ? (
                    servicesOrders.map((service, index) => (
                      <div key={index} className="earnings-service-row">
                        <span className="earnings-service-name">{service.ServiceName}</span>
                        <span className="earnings-service-orders">{service.OrdersCount}</span>
                      </div>
                    ))
                  ) : (
                    <div className="earnings-empty-state">
                      <img src={earningsEmptyIcon} alt="No Orders" className="empty-state-icon" />
                      <h3 className="empty-state-title">No Orders Yet</h3>
                      <p className="empty-state-text">
                        Service orders will appear here once you start receiving bookings.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </Col>
        </Row>

        {/* Earnings Table */}
        <Row>
          <Col>
            <div className="earnings-table-card">
              {transactionsLoading ? (
                <ShimmerTable rows={5} columns={8} />
              ) : (
                <>
                  <table className="earnings-table">
                    <thead>
                      <tr>
                        <th>Booking ID</th>
                        <th>Customer</th>
                        <th>Service</th>
                        <th>Gross</th>
                        <th>Markup</th>
                        <th>Net</th>
                        <th>Status</th>
                        <th>Mode</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.length > 0 ? (
                        transactions.map((row, index) => (
                          <tr key={row.BookingId || index}>
                            <td>
                              <div className="booking-id-cell">
                                <span className="booking-id">{row.BookingId}</span>
                                {row.Date && <span className="booking-date">{formatDate(row.Date)}</span>}
                              </div>
                            </td>
                            <td>{row.Customer}</td>
                            <td>{row.Service}</td>
                            <td>{formatCurrency(row.Gross)}</td>
                            <td>{formatCurrency(row.Markup)}</td>
                            <td>{formatCurrency(row.Net)}</td>
                            <td>
                              <span className={`earnings-status-badge ${row.Status?.toLowerCase()}`}>
                                {row.Status}
                              </span>
                            </td>
                            <td>{row.Mode}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="8" className="empty-table-cell">
                            <div className="earnings-empty-state table-empty">
                              <img src={earningsEmptyIcon} alt="No Transactions" className="empty-state-icon" />
                              <h3 className="empty-state-title">No Transactions Found</h3>
                              <p className="empty-state-text">
                                You don't have any transactions yet. Earnings will appear here once payments are processed.
                              </p>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>

                  {/* Pagination */}
                  {pagination && pagination.TotalPages > 1 && (
                    <div className="earnings-pagination">
                      <button
                        className="pagination-btn"
                        disabled={!pagination.HasPrevious}
                        onClick={() => setCurrentPage(prev => prev - 1)}
                      >
                        Previous
                      </button>
                      <span className="pagination-info">
                        Page {pagination.CurrentPage} of {pagination.TotalPages}
                      </span>
                      <button
                        className="pagination-btn"
                        disabled={!pagination.HasNext}
                        onClick={() => setCurrentPage(prev => prev + 1)}
                      >
                        Next
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Earnings;
